import { useCallback, useState, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import { useGridApiContext, GridDimensions } from '@mui/x-data-grid-premium'

const getDetailPanelWidth = (gridDimensions: GridDimensions) => {
  return (
    gridDimensions.viewportInnerSize.width +
    gridDimensions.leftPinnedWidth +
    gridDimensions.rightPinnedWidth
  )
}

function useDetailPanelWidth() {
  const apiRef = useGridApiContext()
  const [width, setWidth] = useState(() => getDetailPanelWidth(apiRef.current.getRootDimensions()))
  const handleViewportInnerSizeChange = useCallback(() => {
    setWidth(getDetailPanelWidth(apiRef.current.getRootDimensions()))
  }, [apiRef])

  useEffect(() => {
    return apiRef.current.subscribeEvent('viewportInnerSizeChange', handleViewportInnerSizeChange)
  }, [apiRef, handleViewportInnerSizeChange])

  return width
}

interface Props {
  children: React.ReactNode
  callback: () => Promise<void>
}

export default function MasterDetailsContent({ children, callback }: Props) {
  const [loading, setLoading] = useState(true)
  const width = useDetailPanelWidth()

  useEffect(() => {
    let isMounted = true
    void (async () => {
      await callback()

      if (!isMounted) {
        return
      }

      setLoading(false)
    })()

    return () => {
      isMounted = false
    }
  }, [callback])

  return (
    <Stack
      sx={{
        height: '100%',
        boxSizing: 'border-box',
        position: 'sticky',
        left: 0,
        width,
        padding: 2,
      }}
      direction="column"
    >
      {loading ? (
        <Stack width="100%" padding={2} alignItems={'center'} justifyContent={'center'}>
          <CircularProgress size="3rem" />
        </Stack>
      ) : (
        children
      )}
    </Stack>
  )
}
